import React from 'react';
import { Box, Chip, Paper, Typography } from '@mui/material';

// Shared mobile presentation for a single table row: instead of a horizontally
// scrolling <Table>, each record renders as a stacked card of label/value pairs.
// Mirrors the aligned-row aesthetic of PatientDetails/PatientDetailListManager.
// Pages branch on `isMobile` and map their rows to this card; the desktop
// <Table> is unchanged.

const labelSx = {
  color: 'text.secondary',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.04em',
  fontWeight: 600,
  fontSize: '0.62rem',
  lineHeight: 1.3,
  whiteSpace: 'nowrap' as const,
};

export type MobileRecordRow = {
  label: string;
  value: React.ReactNode;
};

type Props = {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Optional status chip shown top-right (e.g. payment status). */
  status?: { label: string; color?: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' };
  rows: MobileRecordRow[];
  /** Action buttons row at the bottom (e.g. Remind / Invoice). */
  actions?: React.ReactNode;
  onClick?: () => void;
};

const MobileRecordCard: React.FC<Props> = ({ title, subtitle, status, rows, actions, onClick }) => (
  <Paper
    variant="outlined"
    onClick={onClick}
    sx={{
      p: 1.5,
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      borderRadius: 2,
      cursor: onClick ? 'pointer' : 'default',
      '&:hover': onClick ? { bgcolor: 'action.hover' } : undefined,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.3, wordBreak: 'break-word' }}>
          {title}
        </Typography>
        {subtitle != null && subtitle !== '' && (
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {status && (
        <Chip
          label={status.label}
          size="small"
          color={status.color}
          sx={{ textTransform: 'capitalize', flexShrink: 0, height: 22, fontSize: '0.7rem' }}
        />
      )}
    </Box>

    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        columnGap: 1.5,
        rowGap: 0.5,
        alignItems: 'baseline',
      }}
    >
      {rows.map((r, i) => (
        <React.Fragment key={`${r.label}-${i}`}>
          <Typography component="span" sx={{ ...labelSx, pt: 0.1 }}>
            {r.label}
          </Typography>
          <Typography component="span" sx={{ fontSize: '0.85rem', color: 'text.primary', wordBreak: 'break-word' }}>
            {r.value == null || r.value === '' ? '—' : r.value}
          </Typography>
        </React.Fragment>
      ))}
    </Box>

    {actions && (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, pt: 0.25 }}>{actions}</Box>
    )}
  </Paper>
);

export default MobileRecordCard;
